import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { getShiftTrack, getShiftHeatmap } from "@/lib/tracking.functions";
import { PatrolMap } from "@/components/PatrolMap";
import { HeatmapLayer } from "@/components/HeatmapLayer";
import { PlaybackControls } from "@/components/PlaybackControls";
import { RealtimeStatusChip } from "@/components/RealtimeStatusChip";
import { useShiftPlayback, type PlaybackTimedEvent, type PlaybackTimedPoint } from "@/hooks/useShiftPlayback";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/tracking/$shiftId")({
  component: PlaybackPage,
  head: () => ({ meta: [{ title: "Shift playback — MissionReady" }] }),
});

type TrackPoint = PlaybackTimedPoint & {
  latitude: number | string;
  longitude: number | string;
  battery_pct?: number | null;
  heartbeat_ok?: boolean | null;
};

function PlaybackPage() {
  const { shiftId } = Route.useParams();
  const fetchTrack = useServerFn(getShiftTrack);
  const fetchHeat = useServerFn(getShiftHeatmap);

  const trackQ = useQuery({
    queryKey: ["playback", shiftId],
    queryFn: () => fetchTrack({ data: { shift_id: shiftId, limit: 5000 } }),
  });
  const heatQ = useQuery({
    queryKey: ["playback-heat", shiftId],
    queryFn: () => fetchHeat({ data: { shift_id: shiftId } }),
  });

  const points = (trackQ.data?.points ?? []) as TrackPoint[];
  const t = trackQ.data?.track;
  const events: PlaybackTimedEvent[] = useMemo(() => [], []);

  const playback = useShiftPlayback<TrackPoint, PlaybackTimedEvent>(points, events);

  const visiblePings = useMemo(
    () =>
      playback.visiblePoints.map((p) => ({
        latitude: Number(p.latitude),
        longitude: Number(p.longitude),
        recorded_at: p.recorded_at,
      })),
    [playback.visiblePoints],
  );
  const livePoint = visiblePings[visiblePings.length - 1] ?? null;

  const heatPoints = useMemo(
    () =>
      (heatQ.data ?? []).map((b) => ({
        lat: Number(b.bucket_lat),
        lng: Number(b.bucket_lng),
        weight: Number(b.weight),
      })),
    [heatQ.data],
  );

  const battery = playback.current?.battery_pct ?? null;

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">Shift Playback</h1>
          <p className="text-xs text-muted-foreground">
            {trackQ.isLoading
              ? "Loading…"
              : t
                ? `${t.point_count} points · ${Math.round(Number(t.distance_m ?? 0))} m`
                : "No track recorded for this shift."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {battery != null && (
            <Badge variant={battery <= 20 ? "destructive" : "outline"}>{battery}%</Badge>
          )}
          <RealtimeStatusChip topic={`shift-playback:${shiftId}`} label="Replay" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <PatrolMap
            pings={visiblePings}
            livePoint={playback.follow ? livePoint : null}
            height={360}
            fitTo="track"
          />
        </CardContent>
      </Card>

      {heatPoints.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <HeatmapLayer points={heatPoints} height={220} showHeat showMarkers={false} />
          </CardContent>
        </Card>
      )}

      <PlaybackControls
        state={playback}
        totalPoints={points.length}
        events={events}
        startedAt={points[0]?.recorded_at ?? null}
        endedAt={points[points.length - 1]?.recorded_at ?? null}
      />
    </div>
  );
}
